import React from 'react';
import {useTranslation} from 'react-i18next';

import {Button, TSize} from '@edna/components';
import {useMeasure} from '@edna/components/utils';

import {TStatusIcon} from 'src/components';
import {useBannerConfig} from 'src/containers/BannersConfig';
import {useAppearStyles} from 'src/hooks';

import {TBannerId, TBannerType} from './definitions';
import * as S from './style';

const iconNames: Record<TBannerType, TStatusIcon> = {
  SUCCESS: 'ok',
  SUCCESS_LIGHT: 'okContour',
  INFO: 'info',
  CANCELLED: 'cancelled',
  CANCEL: 'cancel',
  PENDING: 'pending',
  WARNING: 'warning',
  ERROR: 'error',
};

type TProps = {
  className?: string;
  noMargin?: boolean;
  type?: TBannerType;
  title?: React.ReactNode;
  children?: React.ReactNode;
  fontSize?: TSize;
  shadowSize?: 'S' | 'XS';
  closeId?: TBannerId;
  onClose?: TEmptyFunction;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  closeButtonText?: string;
};

const BannerContent = React.memo<TProps>(
  ({
    className,
    children,
    noMargin,
    title,
    type,
    closeId,
    fontSize = 'M',
    shadowSize = 'S',
    closeButtonText,
    onClose,
    onClick,
  }) => {
    const {t} = useTranslation();
    const [isVisible, setIsVisible] = React.useState(true);
    const [childrenRef, {height: childrenHeight}] = useMeasure<HTMLDivElement>();

    const {closeBanner} = useBannerConfig({id: closeId, category: 'BANNER'});

    const styles = useAppearStyles({
      isVisible,
      childrenHeight,
      onClose: () => {
        if (closeId) {
          closeBanner();
        }

        onClose?.();
      },
    });

    const canClose = !!closeId || !!onClose;
    const handleClose = React.useCallback(() => setIsVisible(false), []);

    return (
      <S.AnimatedDiv style={styles}>
        <S.BannerWrapper ref={childrenRef}>
          <S.Banner
            data-selector="Components:Message"
            className={className}
            noMargin={noMargin}
            shadowSize={shadowSize}
            onClick={onClick}
          >
            {type && <S.StatusIcon name={iconNames[type]} />}
            <S.Content>
              {title && <S.Title hasMargin={!!children}>{title}</S.Title>}
              {children && <S.Text size={fontSize}>{children}</S.Text>}
              {canClose && closeButtonText && (
                <Button onClick={handleClose}>{t(closeButtonText)}</Button>
              )}
            </S.Content>
            {canClose && (
              <S.CloseIcon
                onClick={handleClose}
                size={['S', 'XS'].includes(fontSize) ? '16px' : '20px'}
              />
            )}
          </S.Banner>
        </S.BannerWrapper>
      </S.AnimatedDiv>
    );
  },
);

export const Banner = React.memo<TProps>(({closeId, ...props}) => {
  const {isBannerOpen} = useBannerConfig({id: closeId, category: 'BANNER'});

  if (!isBannerOpen) {
    return null;
  }

  return <BannerContent closeId={closeId} {...props} />;
});

Banner.displayName = 'Banner';
